// Table tokens for the dashboard island. Values come from the summer
// compliance-ui DynamicCustom work (vendor/compliance-dashboard/tokens/dashboardTokens.js
// `widget.table` and DynamicReportTable.module.css), adapted to Cowork's plain-CSS table.
//
// JS reads these values directly (formatting heuristics, clamp lines); CSS reads them
// through the custom properties that `tableTokenStyle()` puts on each table element,
// so this file stays the one place to change them.

export const TABLE_TOKENS = {
  // Summer DynamicReportTable.module.css: .td 9px 12px, .th 8px 12px, --ds-text-sm 12px on a
  // 16px line (a 35px row with its 1px rule). Compact keeps summer's compact 5px a side vertically.
  cellPadding: "9px 12px",
  cellPaddingXPx: 12,
  compactCellPadding: "5px 8px",
  compactCellPaddingXPx: 8,
  largeCellPadding: "11px 14px",
  headerPadding: "8px 12px",
  // A table too narrow for its columns' floors drops to this side padding (summer compact-ish).
  tightCellPaddingXPx: 6,
  fontSize: 12,
  lineHeight: 16,
  headerFontSize: 12,
  headerLineHeight: 16,
  // Summer header: semibold, never the old 800 black.
  headerFontWeight: 600,
  // Long text columns (methodology, reason, free text).
  longText: {
    lines: 3,
    // Floor only: a long column still grows to maxWidthPx when the table has room. 160px
    // (summer) pushed a 6-column half-page table 70px past its card, hiding the column.
    minWidthPx: 88,
    maxWidthPx: 320,
    // A text column counts as long when its longest value or its average passes these.
    maxChars: 60,
    averageChars: 40,
  },
  // Header labels wrap to at most this many lines, then ellipsis with a title tooltip.
  headerLines: 2,
  // Inference reads at most this many rows per column (keeps 100k-row tables cheap).
  inferenceSampleRows: 1000,
  // Shown for null / undefined / "" cells. A real 0 is never replaced by it.
  emptyMark: "—",
}

// Table text at the Design font size (summer --ds-text-sm scaled by DesignPanel's font scale, backlog #18):
// Compact 0.92 gives 11/15, Normal 12/16, Large 13/18. Padding stays the same, so rows only grow with the text.
export function tableTextSizes(fontScale = 1) {
  const scale = Number(fontScale) > 0 ? Number(fontScale) : 1
  return {
    fontSize: Math.round(TABLE_TOKENS.fontSize * scale),
    lineHeight: Math.round(TABLE_TOKENS.lineHeight * scale),
    headerFontSize: Math.round(TABLE_TOKENS.headerFontSize * scale),
    headerLineHeight: Math.round(TABLE_TOKENS.headerLineHeight * scale),
  }
}

export function tableTokenStyle(fontScale = 1) {
  const text = tableTextSizes(fontScale)
  return {
    "--dxt-cell-padding": TABLE_TOKENS.cellPadding,
    "--dxt-compact-cell-padding": TABLE_TOKENS.compactCellPadding,
    "--dxt-large-cell-padding": TABLE_TOKENS.largeCellPadding,
    "--dxt-header-padding": TABLE_TOKENS.headerPadding,
    "--dxt-font-size": `${text.fontSize}px`,
    "--dxt-line-height": `${text.lineHeight}px`,
    "--dxt-header-font-size": `${text.headerFontSize}px`,
    "--dxt-header-line-height": `${text.headerLineHeight}px`,
    "--dxt-header-font-weight": String(TABLE_TOKENS.headerFontWeight),
    "--dxt-header-lines": String(TABLE_TOKENS.headerLines),
    "--dxt-long-lines": String(TABLE_TOKENS.longText.lines),
    "--dxt-long-min-width": `${TABLE_TOKENS.longText.minWidthPx}px`,
    "--dxt-long-max-width": `${TABLE_TOKENS.longText.maxWidthPx}px`,
  }
}
